import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';


const AudienceNegotiationPanel = ({
  defaultCommission = 0,
  audiencePayModel = 'performance_triggered_media_fee',
  onSnapshot,
  onPropose,
  loading = false,
}) => {
  const [platform, setPlatform] = useState('instagram');
  const [followers, setFollowers] = useState('');
  const [averageViews, setAverageViews] = useState('');
  const [commission, setCommission] = useState(String(defaultCommission || 0));
  const [mediaFee, setMediaFee] = useState('0');
  const [minimumConversions, setMinimumConversions] = useState('1');
  const [revenueTrigger, setRevenueTrigger] = useState('0');
  const [deliverables, setDeliverables] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="space-y-3 rounded-xl border border-gray-200 p-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Audience snapshot</h3>
          <p className="mt-1 text-xs leading-5 text-gray-500">Manual evidence is labelled unverified until an official connector confirms it.</p>
        </div>
        <div>
          <Label htmlFor="audience-platform">Platform</Label>
          <select
            id="audience-platform"
            value={platform}
            onChange={(event) => setPlatform(event.target.value)}
            className="mt-1 h-9 w-full rounded-md border border-gray-200 bg-white px-3 text-sm"
          >
            <option value="instagram">Instagram</option>
            <option value="tiktok">TikTok</option>
            <option value="youtube">YouTube</option>
            <option value="x">X</option>
            <option value="newsletter">Newsletter</option>
          </select>
        </div>
        <div>
          <Label htmlFor="audience-followers">Followers</Label>
          <Input id="audience-followers" type="number" min="0" value={followers} onChange={(event) => setFollowers(event.target.value)} />
        </div>
        <div>
          <Label htmlFor="audience-views">Average views</Label>
          <Input id="audience-views" type="number" min="0" value={averageViews} onChange={(event) => setAverageViews(event.target.value)} />
        </div>
        <Button
          type="button"
          variant="outline"
          disabled={loading || !followers}
          onClick={() => onSnapshot({
            platform,
            source: 'manual',
            followerCount: Number(followers),
            averageViews: averageViews ? Number(averageViews) : null,
          })}
        >
          Save audience snapshot
        </Button>
      </div>

      <div className="space-y-3 rounded-xl border border-brand-200 bg-brand-50 p-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Private Negotiation Card</h3>
          <p className="mt-1 text-xs leading-5 text-gray-500">Every proposal is a new immutable version visible only to you and the founder.</p>
        </div>
        <div>
          <Label htmlFor="negotiation-commission">Commission per conversion</Label>
          <Input id="negotiation-commission" type="number" min="0" value={commission} onChange={(event) => setCommission(event.target.value)} />
        </div>
        <div>
          <Label htmlFor="negotiation-media-fee">Media fee</Label>
          <Input
            id="negotiation-media-fee"
            type="number"
            min="0"
            value={audiencePayModel === 'pure_performance' ? '0' : mediaFee}
            disabled={audiencePayModel === 'pure_performance'}
            onChange={(event) => setMediaFee(event.target.value)}
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="negotiation-minimum-conversions">Minimum conversions</Label>
            <Input id="negotiation-minimum-conversions" type="number" min="1" value={minimumConversions} onChange={(event) => setMinimumConversions(event.target.value)} />
          </div>
          <div>
            <Label htmlFor="negotiation-revenue-trigger">Revenue trigger (₦)</Label>
            <Input id="negotiation-revenue-trigger" type="number" min="0" step="0.01" value={revenueTrigger} onChange={(event) => setRevenueTrigger(event.target.value)} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="negotiation-attribution-window">Attribution window (days)</Label>
            <Input id="negotiation-attribution-window" type="number" value="7" disabled />
            <p className="mt-1 text-xs text-gray-500">V1 uses a fixed seven-day last eligible click window.</p>
          </div>
          <div>
            <Label htmlFor="negotiation-expiry">Offer expires</Label>
            <Input id="negotiation-expiry" type="datetime-local" value={expiresAt} onChange={(event) => setExpiresAt(event.target.value)} />
          </div>
        </div>
        <div>
          <Label htmlFor="negotiation-deliverables">Deliverables (one per line)</Label>
          <textarea
            id="negotiation-deliverables"
            value={deliverables}
            onChange={(event) => setDeliverables(event.target.value)}
            placeholder="1 Instagram reel&#10;2 story frames"
            className="mt-1 min-h-20 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm"
          />
        </div>
        <Button
          type="button"
          disabled={loading}
          onClick={() => onPropose({
            commissionPerGoal: Number(commission),
            mediaFee: audiencePayModel === 'pure_performance' ? 0 : Number(mediaFee),
            audiencePayModel,
            currency: 'NGN',
            minimumConversions: Math.max(1, Number(minimumConversions) || 1),
            revenueTriggerMinor: Math.round(Math.max(0, Number(revenueTrigger) || 0) * 100),
            revenueBasis: 'gross',
            attributionWindowDays: 7,
            deliverables: deliverables.split('\n').map((label) => label.trim()).filter(Boolean).map((label) => ({ label })),
            expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
            qualificationRule: { minimum_conversions: Math.max(1, Number(minimumConversions) || 1) },
          })}
        >
          Send private offer
        </Button>
      </div>
    </div>
  );
};

export default AudienceNegotiationPanel;
